import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  visibilityHandler = null;
  pageShowHandler = null;

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.muted = true;
    videoElement.defaultMuted = true;
    videoElement.loop = true;
    videoElement.playsInline = true;
    videoElement.load();

    const tryPlay = () => {
      const playPromise = videoElement.play();

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    };

    if (videoElement.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      tryPlay();
    } else {
      videoElement.addEventListener('loadedmetadata', tryPlay, { once: true });
      videoElement.addEventListener('canplay', tryPlay, { once: true });
    }

    this.visibilityHandler = () => {
      if (!document.hidden) tryPlay();
    };
    this.pageShowHandler = () => {
      tryPlay();
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
    window.addEventListener('pageshow', this.pageShowHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    if (this.pageShowHandler) {
      window.removeEventListener('pageshow', this.pageShowHandler);
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}

const CEO_ACCESS_SCROLL_MOTION_SECTIONS = [
  '#hoe-why-this-matters',
  '#hoe-science-stat',
  '#hoe-inclusions',
  '#hoe-protocol-creation',
  '#hoe-gold-comparison',
  '#hoe-measurements',
  '#hoe-exosome-experience',
  '#human-optimization-cta',
  '#hoe-social-proof',
];

function setupCeoAccessScrollMotion() {
  const root = document.querySelector('[id^="hoe-template"]');

  if (!root || root.dataset.ceoScrollMotion === 'true') return;

  root.dataset.ceoScrollMotion = 'true';
  root.classList.add('hoe__scroll-orchestrated');

  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sections = CEO_ACCESS_SCROLL_MOTION_SECTIONS
    .map((selector) => document.querySelector(selector))
    .filter(Boolean);

  const updateMotion = () => {
    const viewportHeight = window.innerHeight || 1;
    const documentHeight = Math.max(1, document.documentElement.scrollHeight - viewportHeight);
    const scrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
    const pageProgress = Math.min(1, Math.max(0, scrollTop / documentHeight));

    root.style.setProperty('--hoe-scroll-progress', pageProgress.toFixed(4));
    root.style.setProperty('--hoe-page-texture-shift', `${(-18 + pageProgress * 36).toFixed(2)}px`);

    if (reducedMotionQuery.matches) return;

    for (const section of sections) {
      const rect = section.getBoundingClientRect();
      const rawProgress = (viewportHeight - rect.top) / (viewportHeight + rect.height);
      const progress = Math.min(1, Math.max(0, rawProgress));
      const centeredProgress = Math.abs(progress - 0.5);
      const focus = Math.max(0, 1 - centeredProgress * 2);
      const ease = progress * progress * (3 - 2 * progress);

      section.style.setProperty('--hoe-section-progress', progress.toFixed(4));
      section.style.setProperty('--hoe-ambient-shift', `${(-18 + progress * 36).toFixed(2)}px`);
      section.style.setProperty('--hoe-ambient-scale', (1.045 + centeredProgress * 0.03).toFixed(4));
      section.style.setProperty('--hoe-texture-opacity', (0.09 + progress * 0.1).toFixed(4));
      section.style.setProperty('--hoe-scene-y', `${(14 - ease * 28).toFixed(2)}px`);
      section.style.setProperty('--hoe-title-y', `${(8 - ease * 16).toFixed(2)}px`);
      section.style.setProperty('--hoe-media-y', `${(16 - ease * 32).toFixed(2)}px`);
      section.style.setProperty('--hoe-card-y', `${(10 - ease * 20).toFixed(2)}px`);
      section.style.setProperty('--hoe-card-y-soft', `${(6 - ease * 12).toFixed(2)}px`);
      section.style.setProperty('--hoe-card-y-deep', `${(14 - ease * 28).toFixed(2)}px`);
      section.style.setProperty('--hoe-track-shift', `${(-10 + ease * 20).toFixed(2)}px`);
      section.style.setProperty('--hoe-scene-scale', (0.992 + focus * 0.014).toFixed(4));
      section.style.setProperty('--hoe-scene-opacity', (0.9 + focus * 0.1).toFixed(4));
      section.style.setProperty('--hoe-focus-opacity', (0.42 + focus * 0.58).toFixed(4));
      section.style.setProperty('--hoe-depth-blur', `${((1 - focus) * 0.6).toFixed(2)}px`);
      section.style.setProperty('--hoe-tilt-a', `${((1 - focus) * 0.8).toFixed(2)}deg`);
      section.style.setProperty('--hoe-tilt-b', `${((1 - focus) * -0.6).toFixed(2)}deg`);
      section.style.setProperty('--hoe-tilt-c', `${((1 - focus) * 1).toFixed(2)}deg`);
    }
  };

  let ticking = false;

  const requestUpdate = () => {
    if (ticking) return;

    ticking = true;
    window.requestAnimationFrame(() => {
      ticking = false;
      updateMotion();
    });
  };

  updateMotion();
  window.addEventListener('scroll', requestUpdate, { passive: true });
  document.addEventListener('scroll', requestUpdate, { passive: true, capture: true });
  document.scrollingElement?.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupCeoAccessScrollMotion, { once: true });
} else {
  setupCeoAccessScrollMotion();
}

document.addEventListener('shopify:section:load', () => {
  addCeoAccessAmbientBackgrounds();
  setupCeoAccessScrollMotion();
});
